import React from 'react';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

import { cn } from '../../lib/utils';
import {
  MatterStatus,
  MatterType,
} from '../../services/types/client-intake-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { ErrorMessage, Field, Label } from '../ui/catalyst/fieldset';
import { Input } from '../ui/catalyst/input';
import { Listbox, ListboxLabel, ListboxOption } from '../ui/catalyst/listbox';
import { CreateMatterFormValues, UpdateMatterFormValues } from './types';

interface AdditionalDetailsSectionProps {
  control: Control<CreateMatterFormValues | UpdateMatterFormValues>;
  errors: any;
  isLoading: boolean;
  isLitigationOnly?: boolean;
  setValue?: UseFormSetValue<CreateMatterFormValues>;
}

export const AdditionalDetailsSection: React.FC<
  AdditionalDetailsSectionProps
> = ({ control, errors, isLoading, isLitigationOnly, setValue }) => {
  const typeOptions = Object.values(MatterType).map((type) => ({
    value: type,
    label: type
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase()),
  }));

  return (
    <div className="mb-10">
      <Accordion type="single" collapsible className="border-none">
        <AccordionItem value="details" className="border-none">
          <AccordionTrigger className="text-sm font-medium text-zinc-800 dark:text-zinc-200 hover:no-underline">
            Additional Details
          </AccordionTrigger>
          <AccordionContent>
            {/* Additional Case Details */}
            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 pt-4">
              <div className="space-y-1">
                <h2 className="text-base/7 font-semibold text-zinc-950 sm:text-sm/6 dark:text-white">
                  Additional Case Details
                </h2>
                <p className="text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
                  Date of loss and type of incident
                </p>
              </div>

              <div className="space-y-6">
                <Field>
                  <Label htmlFor="date_of_loss">Date of Loss</Label>
                  <Controller
                    name="date_of_loss"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="date_of_loss"
                        type="date"
                        data-slot="control"
                        disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                  {errors.date_of_loss?.message && (
                    <ErrorMessage>{errors.date_of_loss.message}</ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor="statute_of_limitations_date">
                    Statute of Limitations
                  </Label>
                  <Controller
                    name="statute_of_limitations_date"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="statute_of_limitations_date"
                        type="date"
                        data-slot="control"
                        disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                  {errors.statute_of_limitations_date?.message && (
                    <ErrorMessage>
                      {errors.statute_of_limitations_date.message}
                    </ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor="matter_type">Type</Label>
                  <Controller
                    name="matter_type"
                    control={control}
                    render={({ field }) => (
                      <Listbox
                        disabled={isLoading}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder="Select matter type"
                      >
                        {typeOptions.map((option) => (
                          <ListboxOption
                            key={option.value}
                            value={option.value}
                          >
                            <ListboxLabel>{option.label}</ListboxLabel>
                          </ListboxOption>
                        ))}
                      </Listbox>
                    )}
                  />
                  {errors.matter_type?.message && (
                    <ErrorMessage>{errors.matter_type.message}</ErrorMessage>
                  )}
                </Field>
              </div>
            </section>

            <hr
              role="presentation"
              className="my-10 w-full border-t border-zinc-950/5 dark:border-white/5"
            />

            {/* Litigation Only */}
            {setValue && (
              <>
                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                  <div className="space-y-1">
                    <h2 className="text-base/7 font-semibold text-zinc-950 sm:text-sm/6 dark:text-white">
                      Litigation Only
                    </h2>
                    <p className="text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
                      Skip standard pre-litigation tasks
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={isLitigationOnly}
                      disabled={isLoading}
                      onClick={() => {
                        if (isLitigationOnly) {
                          setValue('status', undefined, { shouldValidate: true });
                        } else {
                          setValue('status', MatterStatus.LITIGATION, {
                            shouldValidate: true,
                          });
                        }
                      }}
                      className={cn(
                        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
                        isLitigationOnly
                          ? 'bg-indigo-600'
                          : 'bg-zinc-200 dark:bg-zinc-700',
                        isLoading && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      <span
                        className={cn(
                          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                          isLitigationOnly ? 'translate-x-5' : 'translate-x-0',
                        )}
                      />
                    </button>
                    <span className="text-sm font-medium text-zinc-900 dark:text-white">
                      {isLitigationOnly
                        ? 'Litigation Only \u2014 no pre-litigation tasks will be created'
                        : 'Standard matter (pre-litigation tasks will be created)'}
                    </span>
                  </div>
                </section>

                <hr
                  role="presentation"
                  className="my-10 w-full border-t border-zinc-950/5 dark:border-white/5"
                />
              </>
            )}

            {/* Incident Location */}
            <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
              <div className="space-y-1">
                <h2 className="text-base/7 font-semibold text-zinc-950 sm:text-sm/6 dark:text-white">
                  Incident Location
                </h2>
                <p className="text-base/6 text-zinc-500 sm:text-sm/6 dark:text-zinc-400">
                  Where the incident took place
                </p>
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field>
                  <Label htmlFor="city">City</Label>
                  <Controller
                    name="city"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="city"
                        type="text"
                        data-slot="control"
                        disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                  {errors.city?.message && (
                    <ErrorMessage>{errors.city.message}</ErrorMessage>
                  )}
                </Field>

                <Field>
                  <Label htmlFor="state">State</Label>
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="state"
                        type="text"
                        data-slot="control"
                        disabled={isLoading}
                        {...field}
                      />
                    )}
                  />
                  {errors.state?.message && (
                    <ErrorMessage>{errors.state.message}</ErrorMessage>
                  )}
                </Field>
              </div>
            </section>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
